// this is a custom alert component that supports the following types:
// normal, info, error, warning, success
// it also supports a visible input to conditionally render the alert
// it also supports a complex message with html content
// it also supports a closeable input to optionally close the alert
// it also supports a customClasses input to add additional classes to the alert shell for layout purposes

import { Component, Input, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

export enum AlertType {
  Normal = 'normal',
  Info = 'info',
  Error = 'error',
  Warning = 'warning',
  Success = 'success',
}

@Component({
    selector: 'app-alert',
    host: {
        class: 'tw-block', // to ensure the alert shell is a block element
    },
    template: `
      <div
        role="alert"
        *ngIf="visible"
        [ngClass]="alertClasses"
        class="tw-rounded tw-relative"
        [class.tw-border]="hasBorder"
        [class.tw-border-solid]="hasBorder"
        [class.tw-p-4]="alertStyle === 'normal'"
        [class.tw-p-2]="alertStyle === 'compact'"
        [class.tw-mb-0]="noBottomMargin"
        [class.tw-mb-6]="!noBottomMargin">
      <div [ngClass]="closeable ? 'tw-mr-10' : ''" class="tw-flex tw-items-center">
        <mat-icon
          [ngClass]="iconClass"
          class="tw-shrink-0 tw-mr-4"
          *ngIf="!hideIcon">
            {{ customIcon || icon }}
        </mat-icon>
        <span [ngClass]="textClass" class="tw-inline-block tw-w-full">
          <ng-content></ng-content>
        </span>
      </div>
      <button *ngIf="closeable" mat-icon-button class="tw-absolute tw-top-1 tw-right-[6px] tw-text-gray-500 dark:tw-text-gray-400 tw-w-9 tw-h-9 tw-p-0 tw-leading-6" (click)="visible = false">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  `,
    standalone: true,
    imports: [CommonModule, MatIconModule, MatButtonModule]
})
export class AlertComponent {
  @Input() alertType: AlertType = AlertType.Normal;
  @Input() visible: boolean = true;
  @Input() closeable: boolean = false;
  @Input() alertStyle: 'normal' | 'compact' = 'normal';
  @Input() hasBorder: boolean = true;
  @Input() noBottomMargin: boolean = false;
  @Input() customClasses: string = '';
  @Input() hideIcon: boolean = false;
  @Input() customIcon: string = '';

  @HostBinding('class') get hostClasses() {
    return this.customClasses;
  }

  get alertClasses() {
    const darkBgOpacity = this.hasBorder ? '20' : '50';

    const classes = {
      normal: this.hasBorder
        ? 'tw-bg-gray-100 dark:tw-bg-gray-700 tw-border-gray-300 dark:tw-border-gray-500'
        : 'tw-bg-gray-100 dark:tw-bg-gray-700 tw-border-gray-200 dark:tw-border-gray-800',
      info: this.hasBorder
        ? `tw-bg-blue-100 dark:tw-bg-blue-900/${darkBgOpacity} tw-border-blue-300 dark:tw-border-blue-500`
        : `tw-bg-blue-100 dark:tw-bg-blue-900/${darkBgOpacity} tw-border-blue-200 dark:tw-border-blue-800`,
      error: this.hasBorder
        ? `tw-bg-red-100 dark:tw-bg-red-900/${darkBgOpacity} tw-border-red-300 dark:tw-border-red-500`
        : `tw-bg-red-100 dark:tw-bg-red-900/${darkBgOpacity} tw-border-red-200 dark:tw-border-red-800`,
      warning: this.hasBorder
        ? `tw-bg-orange-100 dark:tw-bg-orange-900/${darkBgOpacity} tw-border-orange-300 dark:tw-border-orange-500`
        : `tw-bg-orange-100 dark:tw-bg-orange-900/${darkBgOpacity} tw-border-orange-200 dark:tw-border-orange-800`,
      success: this.hasBorder
        ? `tw-bg-green-100 dark:tw-bg-green-900/${darkBgOpacity} tw-border-green-300 dark:tw-border-green-500`
        : `tw-bg-green-100 dark:tw-bg-green-900/${darkBgOpacity} tw-border-green-200 dark:tw-border-green-800`,
    };

    return classes[this.alertType];
  }

  get iconClass() {
    const classes = {
      normal: 'tw-text-gray-500 dark:tw-text-gray-400',
      info: 'tw-text-blue-500 dark:tw-text-blue-400',
      error: 'tw-text-red-500 dark:tw-text-red-400',
      warning: 'tw-text-orange-500 dark:tw-text-orange-400',
      success: 'tw-text-green-500 dark:tw-text-green-400',
    };

    return classes[this.alertType];
  }

  get textClass() {
    const classes = {
      normal: 'tw-text-gray-800 dark:tw-text-gray-200',
      info: 'tw-text-blue-800 dark:tw-text-blue-200',
      error: 'tw-text-red-800 dark:tw-text-red-200',
      warning: 'tw-text-orange-800 dark:tw-text-orange-300',
      success: 'tw-text-green-800 dark:tw-text-green-200',
    };

    return classes[this.alertType];
  }

  get icon() {
    const icons = {
      normal: 'notifications',
      info: 'info_outline',
      error: 'error_outline',
      warning: 'warning_outline',
      success: 'check_circle',
    };

    return icons[this.alertType];
  }
}
